import { useLayoutEffect } from 'react'
import { useAgentRuntime, useAgentStore } from '@/context/AgentProvider'
import { useTranslations } from '@/hooks/useTranslations'
import { useChatBootstrap } from '@/hooks/useChatBootstrap'
import { useEnsureActiveChat } from '@/hooks/useEnsureActiveChat'

import { getGuestUserId } from '@/utils/guestUserId'
import { loadLastActiveChatId } from '@/utils/activeChatPersistence'
// import { ContextControls } from './ContextControls'
import { Composer } from './Composer'
import { MessageList } from './MessageList'
import { PlanProgressPanel } from './PlanProgressPanel'
import { ToolShelfProps } from './ToolShelf'
import { AgentControls } from './AgentControls'
import { ChatHistoryPanel } from './ChatHistoryPanel'
import { ChatContentContainer } from './ChatContentContainer'

export interface ChatWindowProps extends ToolShelfProps {
  onClose?: () => void
  agentLogo?: React.ReactElement
  multiUser?: boolean
  participantIds?: string[]
  loadChats?: boolean
  restoreLastActiveChat?: boolean
}

export const ChatWindow = ({
  onToolSelected,
  onClose,
  agentLogo,
  multiUser = false,
  participantIds,
  loadChats = true,
  restoreLastActiveChat = true
}: ChatWindowProps) => {
  const t = useTranslations()
  const runtime = useAgentRuntime()
  const activeChatId = useAgentStore((state) => state.activeChatId)
  const setActiveChat = useAgentStore((state) => state.setActiveChat)
  const { isLoading: isFetchingActiveChat } = useEnsureActiveChat()
  const { isLoading, error } = useChatBootstrap({ enabled: loadChats, multiUser })

  useLayoutEffect(() => {
    if (!restoreLastActiveChat) return
    const userId = runtime.config.user?.id ?? getGuestUserId()
    const restoredChatId = loadLastActiveChatId(userId)
    if (restoredChatId) {
      setActiveChat(restoredChatId)
    }
  }, [restoreLastActiveChat, runtime.config.user?.id, setActiveChat])

  return (
    <div className='mp-agent-shell mp-agent-shell-panel'>
      <ChatHistoryPanel loadChats={loadChats} multiUser={multiUser} />
      <ChatContentContainer>
        {/* <ContextControls /> */}
        <AgentControls onClose={onClose} agentLogo={agentLogo} />
        <div className='mp-agent-main-content'>
          {isLoading || isFetchingActiveChat ? (
            <div style={{ padding: 24 }}>{t.fetchingChats}</div>
          ) : error ? (
            <div style={{ padding: 24, color: 'var(--mp-agents-danger)' }}>{t.fetchingChatError}</div>
          ) : (
            <MessageList key={activeChatId} showParticipantChrome={multiUser} />
          )}
        </div>
        <PlanProgressPanel key={`plan-${activeChatId}`} />
        <Composer onToolSelected={onToolSelected} multiUser={multiUser} participantIds={participantIds} />
      </ChatContentContainer>
    </div>
  )
}

export const MultiUserChatWindow = (props: Omit<ChatWindowProps, 'multiUser'>) => {
  return <ChatWindow {...props} multiUser />
}

export default ChatWindow
